import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog as DialogFromKendo, DialogActionsBar } from '@progress/kendo-react-dialogs';

import { TabPanel } from '../../../components/tab-panel';
import { fileColumns } from './grid-columns';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { fileUploadApiKeyStore } from '../../../api/file-upload-key-store';
import { UploadFileComponent } from './upload-file.component';
import { useFileDownload } from '../../../components/file-download';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteData } from '../../../hooks/useDeleteData';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { useAuthorize } from '../../../hooks/use-authorize';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

interface CustomGridCellProps {
  field: string;
}

interface DocumentRequestArgs {
  fileData?: any;
  workOrderID?: number;
  workOrderNumber?: string;
}

interface WorkorderDocumentDialog {
  open: boolean;
  onClose: () => void;
  parentRefetch?: any;
  documentRequestArgs: DocumentRequestArgs;
}
export const WorkorderDocumentDialog = ({ open, onClose, parentRefetch, documentRequestArgs }: WorkorderDocumentDialog) => {
  const [value, setValue] = useState(documentRequestArgs?.fileData ? 'edit' : 'create');
  const [fileUploadReqArgs, setFileUploadReqArgs] = useState(documentRequestArgs);
  const [editItem, setEditItem] = useState<any>(documentRequestArgs?.fileData);
  const { downloadFile, isLoading } = useFileDownload();
  const [isOpen, setIsOpen] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(0);
  const {
    data,
    isLoading: isLoadingFiles,
    isFetching,
    refetch,
  } = useLoadData<{
    data: ApiResponse<any>[];
    totalRecords: number;
  }>(fileUploadApiKeyStore.getFiles(documentRequestArgs));

  const handleFileUploadCompleted = (value: any) => {
    setFileUploadReqArgs(fileUploadReqArgs);
    setEditItem(undefined);
    setValue('view');
    refetch();
    if (documentRequestArgs?.fileData) parentRefetch();
  };

  const handleEdit = (value: any) => {
    setEditItem(value);
    setValue('edit');
  };

  const handleClosed = () => {
    setEditItem(undefined);
    onClose();
    setValue('create');
  };

  const handleDownloadFile = async (data: any) => {
    downloadFile({ additionalData: data });
  };

  const {
    isLoading: isDeleteLoading,
    isError,
    error,
    mutateAsync,
  } = useDeleteData(fileUploadApiKeyStore.deleteFile(currentItemId));

  const submitHanlder = async () => {
    await mutateAsync();
    setIsOpen(false);
    refetch();
    if (documentRequestArgs?.fileData) parentRefetch();
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const handleDeleteClick = (id: number) => {
    setCurrentItemId(id);
    setIsOpen(true);
  };

  const isInspectionReportPermission = useAuthorize([UserPermissionName?.EDIT_INSPECTION_REPORT]);

  const OptionCell = ({ file }: { file: any }) => {
    const isPermission = !file?.inspectionReportType || isInspectionReportPermission;

    return (
      <td>
        <IconButton aria-label="edit" onClick={() => isPermission && handleEdit(file)} disabled={!isPermission}>
          <Tooltip title={<Typography variant="body1">Edit</Typography>}>
            <EditIcon className="btnEdit" />
          </Tooltip>
        </IconButton>

        <IconButton aria-label="delete" onClick={() => handleDownloadFile(file)}>
          <Tooltip title={<Typography variant="body1">Download</Typography>}>
            <DownloadIcon className="btnEdit" />
          </Tooltip>
        </IconButton>

        <IconButton
          aria-label="delete"
          onClick={() => isPermission && handleDeleteClick(file.fileID)}
          disabled={!isPermission}
        >
          <Tooltip title={<Typography variant="body1">Delete</Typography>}>
            <DeleteIcon style={{ color: !isPermission ? 'gray' : '' }} className="btnDelete" />
          </Tooltip>
        </IconButton>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'option') {
      return (dataItem: any) => {
        return <OptionCell file={dataItem.dataItem} />;
      };
    }
  };

  const handleTabValueChange = (value: string) => {
    setValue(value);
    if (value === 'view') {
      setEditItem(undefined);
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={handleClosed}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ p: 0, m: 0 }}>
        <Tabs value={value} onChange={(event: any, value: string) => handleTabValueChange(value)}>
          <Tab value="view" label={<div style={styling.tabHeading}>FILE LIST</div>} />
          <Tab value="create" label={<div style={styling.tabHeading}>UPLOAD FILES</div>} hidden={!!editItem} />
          <Tab value="edit" label={<div style={styling.tabHeading}>EDIT UPLOAD FILE</div>} hidden={!editItem} />
        </Tabs>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        <TabPanel selectedValue={value} value="view">
          <Grid container spacing={2}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <SiTrackGrid
                fileTitle='workorder_file'
                dataItemKey="fileID"
                columns={fileColumns}
                dataItems={data?.data || []}
                // queryConfig={fileUploadApiKeyStore.getFiles(documentRequestArgs)}
                // onDownload={(data: any) => {
                //   handleDownloadFile(data);
                // }}
                // onEdit={handleEdit}
                // deleteMutationConfig={(item) => fileUploadApiKeyStore.deleteFile(item.fileID)}
                // actionPermissions={{
                //   export: UserPermissionName.DOWNLOAD_GRID,
                // }}
                isBusy={isLoading || isDeleteLoading || isLoadingFiles || isFetching}
                customCell={(field) =>
                  CustomGridCell({
                    field,
                  })
                }
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Grid container justifyContent="flex-end">
                <Button variant="outlined" style={{ width: '130px' }} onClick={handleClosed}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel selectedValue={value} value="create">
          <UploadFileComponent
            documentRequestArgs={fileUploadReqArgs}
            onSaveComplete={handleFileUploadCompleted}
            onClose={handleClosed}
            page="workOrder"
            tabValue={value}
          />
        </TabPanel>
        <TabPanel selectedValue={value} value="edit">
          <UploadFileComponent
            fileData={editItem}
            documentRequestArgs={fileUploadReqArgs}
            onSaveComplete={handleFileUploadCompleted}
            onClose={handleClosed}
            page="workOrder"
            tabValue={value}
          />
        </TabPanel>
        {isOpen && (
          <DialogFromKendo className="modalpopup" title={'CONFIRM DELETE'} onClose={closeDialog}>
            <div>
              <div style={{ padding: '25px 50px' }}>
                <div>Are you sure you want to permanently delete?</div>
                <LoadingIndicator isLoading={isLoading} />
              </div>
            </div>{' '}
            <DialogActionsBar>
              <div className=" spaceBetween">
                <button className="btn btn-sm btn-default btn-lightGrey" onClick={closeDialog} autoFocus>
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger btn-delete"
                  type="button"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                    color: isReadOnlyPermission ? 'black' : '#fff'
                  }}
                  onClick={submitHanlder}
                  disabled={isLoading || isReadOnlyPermission}
                >
                  Delete
                </button>
              </div>
            </DialogActionsBar>
          </DialogFromKendo>
        )}
      </DialogContent>
    </Dialog>
  );
};
